import { z } from "zod";

/**
 * The build-log SSE wire contract — what the console reads while a build runs.
 *
 * ## Why this is not a NexusApi endpoint
 *
 * Every other Vibe read rides `ZVibe` and the generated client. A stream cannot:
 * the envelope (`{success, data}`) describes ONE response body, and the codegen
 * has no shape for a connection that emits frames over minutes. Chat already hit
 * this and settled it — `ChatStreamEventSchema` is a wire contract in this
 * package while the endpoint itself is a raw `@Res()` route outside the
 * generated surface. This mirrors that precedent exactly rather than inventing a
 * second answer to the same question.
 *
 * ## Frames are data-only
 *
 * No SSE `event:` names; the discriminant lives INSIDE the JSON, per
 * `chat-stream-sse-adapter.ts`. One parser, one union, one place a new frame
 * type has to be handled — a named-event wire splits that across the transport
 * and the payload.
 */

/**
 * One rendered log line.
 *
 * Deliberately `{id, text}` and NOT the `{id, text, timestamp?}` the LogView
 * canon's line model allows: **the buffer has no clock.** `AgentBuildLogChunk`
 * carries `{buildJobId, stream, byteOffset, text}` — raw bytes and their
 * position, nothing temporal. Synthesising a timestamp control-plane-side would
 * be a measurement of when WE saw the bytes, not when the builder wrote them:
 * chunks arrive batched (a whole relay pass would share one instant) and a
 * settled log replays out of Redis instantly (every line of a four-minute build
 * would stamp identically, "now"). That is the same invented-chronology the
 * stdout/stderr ordering decision refuses, and it fails the same way — it reads
 * as authoritative while being arbitrary.
 *
 * The canon already sanctions the honest render: `timestamp` is optional, the
 * gutter collapses when absent, and `NoTimestamps` is a required story. Real
 * elapsed stamps need the AGENT to stamp lines as it reads them, which is a
 * write-path change and its own slice.
 *
 * `id` is producer-assigned and stable (`<stream>:<sequence>`) — the canon
 * requires it because a filtered view reorders indices by construction, so an
 * array-index key would rebind every row to the wrong line the moment a user
 * types in the search field.
 */
export const VibeBuildLogLineSchema = z.object({
  id: z.string().min(1),
  text: z.string()
});
export type VibeBuildLogLine = z.infer<typeof VibeBuildLogLineSchema>;

/**
 * Why a completed stream ended, and the reason an empty log is not self-describing.
 *
 * The live buffer is mortal — Redis holds it for a rolling window measured from
 * the last chunk written. Once that window closes the key is gone, and a build
 * that printed a thousand lines reads exactly like a build that printed none:
 * zero bytes, both times. The console cannot tell them apart from the data, and
 * the difference is not cosmetic — "this build produced no output" is a
 * definitive claim a reader acts on, and it is false for every build older than
 * the window.
 *
 * So the server, which has the facts the console lacks, says which it is:
 *
 * - `complete`    — the log is exactly what was delivered, including when that is
 *                   nothing. PROVABLE: the buffer went quiet, AND the build
 *                   finished recently enough that any output it produced would
 *                   still be buffered, so an empty log really is an empty log.
 * - `unavailable` — the buffer's window has closed, so what the build printed is
 *                   unknowable from here. Says nothing about whether there WAS
 *                   output; only that we can no longer answer.
 * - `incomplete`  — the server stopped waiting. The buffer was still growing when
 *                   the settle deadline fired, so what was delivered is a prefix
 *                   of the log, not the log. Retrying re-reads from the start and
 *                   may now get all of it.
 *
 * The asymmetry is deliberate. `unavailable` is the fallback whenever the proof
 * for `complete` does not hold, because the two mistakes are not equal: claiming
 * "no output" about a log we lost is a lie the reader will believe, while
 * admitting we cannot say is merely unhelpful. Durable log storage removes the
 * `unavailable` distinction entirely — until then, this is the honest shape.
 *
 * `incomplete` exists because the settle deadline is a bound on WAITING, not a
 * statement about the log. The server already logs "ending a possibly incomplete
 * log" when it fires; without this reason the frame said `complete` on that exact
 * path, so the server's own warning and its wire contract contradicted each other
 * and the console settled a truncated log as whole.
 */
export const VIBE_BUILD_LOG_END_REASONS = ["complete", "unavailable", "incomplete"] as const;
export const VibeBuildLogEndReasonSchema = z.enum(VIBE_BUILD_LOG_END_REASONS);
export type VibeBuildLogEndReason = z.infer<typeof VibeBuildLogEndReasonSchema>;

/**
 * A frame on the build-log stream.
 *
 * - `lines` — append these, in order. The only frame that carries content.
 * - `end`   — the log is complete and the connection is closing. The build is
 *             terminal and both streams are fully drained. Drives the console's
 *             `isStreaming` → false (settled: no live indicator, no caret).
 *             Carries `reason` — see `VibeBuildLogEndReasonSchema`; an empty log
 *             means nothing without it.
 * - `error` — the stream broke. Terminal, like chat's: `end` does NOT follow.
 *             The console keeps every line already rendered and shows the error
 *             beneath them, because a partial log is the most valuable thing on
 *             screen when a stream dies.
 */
export const VibeBuildLogStreamEventSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("lines"),
    lines: z.array(VibeBuildLogLineSchema)
  }),
  z.object({
    type: z.literal("end"),
    reason: VibeBuildLogEndReasonSchema
  }),
  z.object({
    type: z.literal("error"),
    message: z.string()
  })
]);
export type VibeBuildLogStreamEvent = z.infer<typeof VibeBuildLogStreamEventSchema>;
